import React, { useState } from "react";
import { motion } from "framer-motion";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Box, Button, Chip, Container, Grid, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import PrintIcon from "@mui/icons-material/Print";
import HistoryIcon from "@mui/icons-material/History";
import ShieldIcon from "@mui/icons-material/Shield";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SimpleLabelPage from "./SimpleLabelPage";
import CustomAlert from "../../components/ui/CustomAlert";

const ACCENT = "#d97706";
const ACCENT_LIGHT = "#f59e0b";
const GRADIENT = "linear-gradient(145deg, #92400e 0%, #b45309 30%, #d97706 65%, #f59e0b 100%)";

const features = [
  { icon: ConfirmationNumberIcon, label: "6 modelos de etiqueta" },
  { icon: PrintIcon, label: "Impressao integrada" },
  { icon: HistoryIcon, label: "Controle de validade" },
  { icon: ShieldIcon, label: "Rastreabilidade por lote" },
];

const pageVariants = {
  initial: { opacity: 0 },
  in: { opacity: 1, transition: { duration: 0.3 } },
  out: { opacity: 0, transition: { duration: 0.3 } },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.05 } },
};

const riseUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 200, damping: 22 } },
};

const SimpleLabel = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");

  const showAlert = (message, severity = "success") => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
    setTimeout(() => setAlertOpen(false), 5000);
  };

  const closeAlert = () => setAlertOpen(false);

  const getDeliveryCenterId = () => {
    const userDataString = localStorage.getItem("user");
    if (!userDataString) return null;
    try {
      const userData = JSON.parse(userDataString);
      return userData?.delivery_center_id || userData?.deliveryCenterId || null;
    } catch (error) {
      console.error("Error parsing user data:", error);
      return null;
    }
  };

  const deliveryCenterId = getDeliveryCenterId();

  const scrollToContent = () => {
    document.getElementById("label-content")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <motion.div initial="initial" animate="in" exit="out" variants={pageVariants}>
        {/* ─── HERO ─── */}
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            pt: { xs: 6, md: 10 },
            pb: { xs: 8, md: 14 },
            background: GRADIENT,
          }}
        >
          {/* Decorative grid */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              opacity: 0.04,
              backgroundImage:
                "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
              backgroundSize: "48px 48px",
            }}
          />

          {/* Glow */}
          <Box
            component={motion.div}
            animate={{ y: [0, -12, 0] }}
            transition={{ duration: 10, repeat: Infinity, repeatType: "reverse" }}
            sx={{
              position: "absolute",
              width: 400,
              height: 400,
              borderRadius: "50%",
              background: `radial-gradient(circle, ${alpha("#fff", 0.08)} 0%, transparent 70%)`,
              top: -160,
              right: -100,
            }}
          />

          <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
            <motion.div variants={stagger} initial="hidden" animate="visible">
              {/* Badge */}
              <motion.div variants={riseUp}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mb: 2.5 }}>
                  <Chip
                    label="ETIQUETA SIMPLES"
                    size="small"
                    sx={{
                      backgroundColor: alpha("#fff", 0.15),
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "0.65rem",
                      letterSpacing: "2px",
                      backdropFilter: "blur(8px)",
                      border: `1px solid ${alpha("#fff", 0.2)}`,
                    }}
                  />
                </Stack>
              </motion.div>

              {/* Title */}
              <motion.div variants={riseUp}>
                <Typography
                  variant={isMobile ? "h4" : "h2"}
                  fontWeight={800}
                  align="center"
                  sx={{ color: "#fff", mb: 2, letterSpacing: "-0.02em", lineHeight: 1.15 }}
                >
                  Crie etiquetas profissionais
                  <br />
                  em poucos cliques
                </Typography>
              </motion.div>

              <motion.div variants={riseUp}>
                <Typography
                  variant="h6"
                  align="center"
                  fontWeight={400}
                  sx={{ color: alpha("#fff", 0.7), maxWidth: 500, mx: "auto", mb: 5, lineHeight: 1.6, fontSize: { xs: "0.9rem", md: "1rem" } }}
                >
                  Escolha entre 6 modelos, preencha os dados do produto e imprima direto. Controle validade e lotes com total rastreabilidade.
                </Typography>
              </motion.div>

              {/* Feature pills */}
              <motion.div variants={stagger}>
                <Grid container spacing={2} justifyContent="center" sx={{ mb: 4 }}>
                  {features.map((f, i) => {
                    const Icon = f.icon;
                    return (
                      <Grid item key={i}>
                        <motion.div variants={riseUp}>
                          <Chip
                            icon={<Icon sx={{ fontSize: 16, color: `${ACCENT_LIGHT} !important` }} />}
                            label={f.label}
                            sx={{
                              backgroundColor: alpha("#fff", 0.1),
                              color: "#fff",
                              fontWeight: 500,
                              fontSize: "0.78rem",
                              border: `1px solid ${alpha("#fff", 0.15)}`,
                              backdropFilter: "blur(4px)",
                              px: 0.5,
                              height: 34,
                            }}
                          />
                        </motion.div>
                      </Grid>
                    );
                  })}
                </Grid>
              </motion.div>

              {/* Scroll CTA */}
              <motion.div variants={riseUp}>
                <Stack alignItems="center">
                  <Button
                    onClick={scrollToContent}
                    endIcon={<ArrowDownwardIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      color: alpha("#fff", 0.7),
                      textTransform: "none",
                      fontWeight: 500,
                      "&:hover": { color: "#fff", backgroundColor: alpha("#fff", 0.08) },
                    }}
                  >
                    Ver etiquetas
                  </Button>
                </Stack>
              </motion.div>
            </motion.div>
          </Container>

          {/* Separator */}
          <Box sx={{ position: "absolute", bottom: -1, left: 0, right: 0, lineHeight: 0 }}>
            <svg viewBox="0 0 1440 80" preserveAspectRatio="none" style={{ width: "100%", height: 50, display: "block" }}>
              <path d="M0 80L1440 0V80H0Z" fill={theme.palette.background.default} />
            </svg>
          </Box>
        </Box>

        {/* ─── CONTENT ─── */}
        <Box id="label-content" maxWidth="xl" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 8 } }}>
          <CustomAlert
            open={alertOpen}
            message={alertMessage}
            severity={alertSeverity}
            onClose={closeAlert}
          />

          <SimpleLabelPage showAlert={showAlert} deliveryCenterId={deliveryCenterId} />
        </Box>
      </motion.div>
    </LocalizationProvider>
  );
};

export default SimpleLabel;
